import { useUpdateRevision } from "@/queries/formality-request";
import type {
  FormalityRequestType,
  FormDetailResponse,
} from "@/types/entities/formality-request";
import { useToast } from "@elaniintc/goes-design-system";
import type { AxiosError } from "axios";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useCorrectionForm } from "../context";
import { resolveGroupId } from "../utils/resolve-group-id";

export type SubmitModalState = "idle" | "general-reason";

type UseSubmitCorrectionsParams = {
  data: FormDetailResponse | undefined;
  formalityRequest: FormalityRequestType | undefined;
  procedureId: string;
};

/**
 * Sends the reviewer's corrections back to the applicant. Denied fields require
 * a general comment first, which is why submitting goes through a modal.
 */
export const useSubmitCorrections = ({
  data,
  formalityRequest,
  procedureId,
}: UseSubmitCorrectionsParams) => {
  const [modalState, setModalState] = useState<SubmitModalState>("idle");
  const { showToast } = useToast();
  const navigate = useNavigate();
  const {
    corrections,
    selectedRevisionId,
    selectedGroupId,
    selectedRevision,
    isUserLeaderOfSelectedGroup,
  } = useCorrectionForm();

  const { mutateAsync: updateRevision, isPending: isLoadingUpdateRevision } =
    useUpdateRevision();

  const handleSubmitWithGeneralComment = async (generalReason?: string) => {
    if (!data) return;
    const filledFieldCorrections = Array.from(corrections.entries()).map(
      ([fieldIdentifier, { status, reason }]) => {
        const isSynthetic = fieldIdentifier.startsWith("field_");
        return {
          filled_field_id: isSynthetic ? null : fieldIdentifier,
          ...(isSynthetic
            ? { field_id: fieldIdentifier.replace(/^field_/, "") }
            : {}),
          status,
          ...(status === "DENIED" && reason ? { comment: reason } : {}),
        };
      },
    );
    try {
      // Verificar que form_assignments existe y tiene elementos
      if (!data?.form_assignments?.[0]?.revisions?.length) {
        throw new Error("No hay revisiones disponibles");
      }

      const revisionCounts = data.form_assignments[0].revisions.length;
      const currentRevisionId =
        selectedRevisionId ||
        data.form_assignments[0].revisions[revisionCounts - 1]?.id;

      if (!currentRevisionId) {
        throw new Error("No se pudo encontrar el ID de revisión");
      }

      const activeRevision =
        data.form_assignments[0].revisions.find(
          (rev) => rev.id === currentRevisionId,
        ) || data.form_assignments[0].revisions[revisionCounts - 1];

      if (!activeRevision) {
        throw new Error("No se encontró la revisión activa");
      }

      const groupId = resolveGroupId(
        activeRevision,
        data.form_assignments[0],
        formalityRequest,
        selectedGroupId,
      );

      await updateRevision({
        revision_id: currentRevisionId,
        // correction_fields can be empty when revision has groups
        correction_fields: filledFieldCorrections,
        correction_comment:
          generalReason?.trim() || "No se agregaron comentarios adicionales.",
        ...(groupId ? { group_id: groupId } : {}),
      });
      setModalState("idle");

      // Mostrar toast diferente si requiere aprobación de líder y el usuario no es líder
      const requiresLeaderApproval =
        selectedRevision?.requires_leader_approval ?? false;

      if (requiresLeaderApproval && !isUserLeaderOfSelectedGroup) {
        showToast({
          title: "Observaciones enviadas a aprobación",
          description:
            "Las observaciones realizadas en este formulario han sido enviadas a visto bueno para poder ser aprobadas.",
          options: {
            type: "success",
          },
        });
      } else {
        showToast({
          title: "Correcciones enviadas",
          description: "Las correciones se enviaron exitosamente",
          options: {
            type: "success",
          },
        });
      }
      navigate(`/tramite/${procedureId}`);
    } catch (e) {
      const error = e as AxiosError<{ message: string }>;
      // Handle 422 status code with specific message
      if (error?.response?.status === 422 && error?.response?.data?.message) {
        showToast({
          title: "Error de validación",
          description: error.response.data.message,
          options: {
            type: "error",
          },
        });
      } else {
        showToast({
          title: "Error al enviar correcciones",
          description:
            "Ocurrió un error al enviar las correcciones, por favor intenta nuevamente",
          options: {
            type: "error",
          },
        });
      }
    }
  };

  const onSubmitCorrections = () => {
    if (!data) return;

    // Check if there are any denied fields
    const hasDeniedFields = Array.from(corrections.values()).some(
      (correction) => correction.status === "DENIED",
    );
    if (hasDeniedFields) {
      setModalState("general-reason");
    } else {
      handleSubmitWithGeneralComment();
    }
  };

  return {
    modalState,
    setModalState,
    isLoadingUpdateRevision,
    onSubmitCorrections,
    handleSubmitWithGeneralComment,
  };
};
